import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  useToast,
  HStack,
  Text,
  Spinner,
} from '@chakra-ui/react';
import {
  SearchIcon,
  AddIcon,
  DownloadIcon,
  ViewIcon,
  EditIcon,
  AttachmentIcon,
  TimeIcon,
} from '@chakra-ui/icons';
import { MdMoreVert } from 'react-icons/md';
import FloatingAddButton from '../../../components/FloatingAddButton';
import tramitesService, { Tramite, TipoTramite, FiltrosTramites } from '../../../services/tramitesObrasService';
import ModalTramite from './components/ModalTramite';
import ModalDocumentos from './components/ModalDocumentos';
import ModalHistorial from './components/ModalHistorial';
import ModalCambiarEstatus from './components/ModalCambiarEstatus';

const ESTATUS_COLORS: Record<string, string> = {
  REGISTRADO: 'blue',
  EN_PROCESO: 'yellow',
  EN_REVISION: 'orange',
  PROCEDENTE: 'green',
  NO_PROCEDENTE: 'red',
  CONCLUIDO: 'gray',
};

const GestionTramites: React.FC = () => {
  const [tramites, setTramites] = useState<Tramite[]>([]);
  const [tiposTramites, setTiposTramites] = useState<TipoTramite[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosTramites>({});
  const [tramiteSeleccionado, setTramiteSeleccionado] = useState<Tramite | null>(null);
  const [modoModal, setModoModal] = useState<'new' | 'edit' | 'view'>('new');

  const toast = useToast();

  // Modales
  const {
    isOpen: isOpenTramite,
    onOpen: onOpenTramite,
    onClose: onCloseTramite,
  } = useDisclosure();

  const {
    isOpen: isOpenDocumentos,
    onOpen: onOpenDocumentos,
    onClose: onCloseDocumentos,
  } = useDisclosure();

  const {
    isOpen: isOpenHistorial,
    onOpen: onOpenHistorial,
    onClose: onCloseHistorial,
  } = useDisclosure();

  const {
    isOpen: isOpenEstatus,
    onOpen: onOpenEstatus,
    onClose: onCloseEstatus,
  } = useDisclosure();

  // Cargar datos iniciales
  useEffect(() => {
    cargarTiposTramites();
    cargarTramites();
  }, []);

  const cargarTiposTramites = async () => {
    try {
      const tipos = await tramitesService.obtenerTiposTramites();
      setTiposTramites(tipos);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los tipos de trámites',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const cargarTramites = async (filtrosAplicados?: FiltrosTramites) => {
    setLoading(true);
    try {
      const data = await tramitesService.obtenerTramites(filtrosAplicados || filtros);
      setTramites(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los trámites',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    cargarTramites(filtros);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({});
    cargarTramites({});
  };

  const handleNuevoTramite = () => {
    setTramiteSeleccionado(null);
    setModoModal('new');
    onOpenTramite();
  };

  const handleVerTramite = (tramite: Tramite) => {
    setTramiteSeleccionado(tramite);
    setModoModal('view');
    onOpenTramite();
  };

  const handleEditarTramite = (tramite: Tramite) => {
    setTramiteSeleccionado(tramite);
    setModoModal('edit');
    onOpenTramite();
  };

  const handleVerDocumentos = (tramite: Tramite) => {
    setTramiteSeleccionado(tramite);
    onOpenDocumentos();
  };

  const handleVerHistorial = (tramite: Tramite) => {
    setTramiteSeleccionado(tramite);
    onOpenHistorial();
  };

  const handleCambiarEstatus = (tramite: Tramite) => {
    setTramiteSeleccionado(tramite);
    onOpenEstatus();
  };

  const handleDescargarPDF = (tramiteId: number) => {
    const url = tramitesService.getUrlDetallePDF(tramiteId);
    window.open(url, '_blank');
  };

  const handleDescargarReporte = () => {
    const url = tramitesService.getUrlReportePDF(filtros);
    window.open(url, '_blank');
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-MX');
  };

  return (
    <Box p={4} pt="79px">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Gestión de Trámites de Obras Públicas</Heading>
        <Button
          leftIcon={<DownloadIcon />}
          colorScheme="blue"
          variant="outline"
          onClick={handleDescargarReporte}
          size="sm"
        >
          Descargar Reporte
        </Button>
      </Flex>

      {/* FILTROS */}
      <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={4}>
        <Flex gap={3} wrap="wrap" mb={3}>
          <Input
            placeholder="Buscar por folio, nombre, correo..."
            value={filtros.buscar || ''}
            onChange={(e) => setFiltros({ ...filtros, buscar: e.target.value })}
            maxW="300px"
          />

          <Select
            placeholder="Tipo de Trámite"
            value={filtros.idTipoTramite || ''}
            onChange={(e) =>
              setFiltros({
                ...filtros,
                idTipoTramite: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            maxW="250px"
          >
            {tiposTramites.map((tipo) => (
              <option key={tipo.id} value={tipo.id}>
                {tipo.nombre}
              </option>
            ))}
          </Select>

          <Select
            placeholder="Estatus"
            value={filtros.estatus || ''}
            onChange={(e) => setFiltros({ ...filtros, estatus: e.target.value })}
            maxW="180px"
          >
            <option value="REGISTRADO">Registrado</option>
            <option value="EN_PROCESO">En Proceso</option>
            <option value="EN_REVISION">En Revisión</option>
            <option value="PROCEDENTE">Procedente</option>
            <option value="NO_PROCEDENTE">No Procedente</option>
            <option value="CONCLUIDO">Concluido</option>
          </Select>

          <Select
            placeholder="Origen"
            value={filtros.origen || ''}
            onChange={(e) => setFiltros({ ...filtros, origen: e.target.value })}
            maxW="150px"
          >
            <option value="PRESENCIAL">Presencial</option>
            <option value="ONLINE">Online</option>
          </Select>

          <Input
            type="date"
            placeholder="Fecha Inicio"
            value={filtros.fechaInicio || ''}
            onChange={(e) => setFiltros({ ...filtros, fechaInicio: e.target.value })}
            maxW="180px"
          />

          <Input
            type="date"
            placeholder="Fecha Fin"
            value={filtros.fechaFin || ''}
            onChange={(e) => setFiltros({ ...filtros, fechaFin: e.target.value })}
            maxW="180px"
          />
        </Flex>

        <HStack>
          <Button leftIcon={<SearchIcon />} colorScheme="blue" onClick={handleBuscar} size="sm">
            Buscar
          </Button>
          <Button variant="outline" onClick={handleLimpiarFiltros} size="sm">
            Limpiar Filtros
          </Button>
        </HStack>
      </Box>

      {/* TABLA */}
      <Box bg="white" borderRadius="md" shadow="sm" overflowX="auto">
        {loading ? (
          <Flex justify="center" align="center" p={8}>
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : (
          <Table size="sm" variant="striped">
            <Thead>
              <Tr>
                <Th>Folio</Th>
                <Th>Fecha</Th>
                <Th>Solicitante</Th>
                <Th>Trámite</Th>
                <Th>Estatus</Th>
                <Th>Origen</Th>
                <Th>Docs</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {tramites.length === 0 ? (
                <Tr>
                  <Td colSpan={8} textAlign="center" py={8}>
                    <Text color="gray.500">No se encontraron trámites</Text>
                  </Td>
                </Tr>
              ) : (
                tramites.map((tramite) => (
                  <Tr key={tramite.id}>
                    <Td fontWeight="bold">{tramite.folio}</Td>
                    <Td>{formatFecha(tramite.fechaRegistro)}</Td>
                    <Td>{tramite.nombreSolicitante}</Td>
                    <Td maxW="200px" isTruncated>
                      {tramite.tipoTramite?.nombre}
                    </Td>
                    <Td>
                      <Badge colorScheme={ESTATUS_COLORS[tramite.estatus] || 'gray'}>
                        {tramite.estatus}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge variant="outline">{tramite.origen}</Badge>
                    </Td>
                    <Td>{tramite._count?.documentos || 0}</Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<MdMoreVert />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem icon={<ViewIcon />} onClick={() => handleVerTramite(tramite)}>
                            Ver Detalle
                          </MenuItem>
                          <MenuItem icon={<EditIcon />} onClick={() => handleEditarTramite(tramite)}>
                            Editar
                          </MenuItem>
                          <MenuItem
                            icon={<AttachmentIcon />}
                            onClick={() => handleVerDocumentos(tramite)}
                          >
                            Documentos ({tramite._count?.documentos || 0})
                          </MenuItem>
                          <MenuItem icon={<TimeIcon />} onClick={() => handleVerHistorial(tramite)}>
                            Historial
                          </MenuItem>
                          <MenuItem onClick={() => handleCambiarEstatus(tramite)}>
                            Cambiar Estatus
                          </MenuItem>
                          <MenuItem
                            icon={<DownloadIcon />}
                            onClick={() => handleDescargarPDF(tramite.id)}
                          >
                            Descargar PDF
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Botón Flotante */}
      <FloatingAddButton text="Nuevo Trámite" onClick={handleNuevoTramite} />

      {/* MODALES */}
      {isOpenTramite && (
        <ModalTramite
          isOpen={isOpenTramite}
          onClose={onCloseTramite}
          tramite={tramiteSeleccionado}
          tiposTramites={tiposTramites}
          modo={modoModal}
          onSuccess={() => {
            cargarTramites();
            onCloseTramite();
          }}
        />
      )}

      {isOpenDocumentos && tramiteSeleccionado && (
        <ModalDocumentos
          isOpen={isOpenDocumentos}
          onClose={onCloseDocumentos}
          tramite={tramiteSeleccionado}
          onSuccess={cargarTramites}
        />
      )}

      {isOpenHistorial && tramiteSeleccionado && (
        <ModalHistorial
          isOpen={isOpenHistorial}
          onClose={onCloseHistorial}
          tramiteId={tramiteSeleccionado.id}
        />
      )}

      {isOpenEstatus && tramiteSeleccionado && (
        <ModalCambiarEstatus
          isOpen={isOpenEstatus}
          onClose={onCloseEstatus}
          tramite={tramiteSeleccionado}
          onSuccess={() => {
            cargarTramites();
            onCloseEstatus();
          }}
        />
      )}
    </Box>
  );
};

export default GestionTramites;
